
import { supabase } from '@/integrations/supabase/client';


// Default types for analytics tracking
type ContentType = 'game_plan' | 'technique';
type ActionType = 'view' | 'import' | 'conversion';

// Get a persistent visitor ID for analytics tracking
function getVisitorId(): string {
  let visitorId = localStorage.getItem('visitor_id');
  
  if (!visitorId) {
    visitorId = crypto.randomUUID();
    localStorage.setItem('visitor_id', visitorId);
  }
  
  return visitorId;
}

// Get referral code from URL or session storage
function getReferralCode(): string | null {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const refParam = urlParams.get('ref');
  
  if (refParam) {
    // Store referral code in session storage for later use
    sessionStorage.setItem('referral_code', refParam);
    return refParam;
  }
  
  // If not in URL, check session storage
  return sessionStorage.getItem('referral_code');
}

/**
 * Track a content sharing event
 * @param contentType Type of content (game_plan or technique)
 * @param shareId The unique share ID of the content
 * @param actionType The type of action (view, import, conversion)
 * @param metadata Optional additional data to track
 * @returns A promise that resolves when tracking is complete
 */
export async function trackSharingEvent(
  contentType: ContentType,
  shareId: string,
  actionType: ActionType,
  metadata?: Record<string, unknown>
): Promise<void> {
  try {
    const visitorId = getVisitorId();
    const referralCode = getReferralCode();
    
    console.log(`Tracking ${actionType} for ${contentType} with shareId: ${shareId}`);
    
    // Get content ID from share ID
    const tableName = contentType === 'game_plan' ? 'shared_game_plans' : 'shared_techniques';
    const { data: contentData } = await supabase
      .from(tableName)
      .select('id')
      .eq('share_id', shareId)
      .single();
    
    if (!contentData) {
      console.warn(`No content found for share ID: ${shareId}`);
      return;
    }
    
    // Get visitor information
    const userAgent = navigator.userAgent || '';
    const referrerUrl = document.referrer || '';
    
    // Get approximate device type from user agent
    let deviceType = 'unknown';
    if (userAgent.includes('Mobi')) {
      deviceType = 'mobile';
    } else if (userAgent.includes('Tablet')) {
      deviceType = 'tablet';
    } else if (!userAgent.includes('Bot') && !userAgent.includes('Spider')) {
      deviceType = 'desktop';
    }
    
    // Insert directly into analytics table
    await supabase
      .from('content_sharing_analytics')
      .insert({
        content_type: contentType,
        content_id: contentData.id,
        share_id: shareId,
        referral_code: referralCode,
        action_type: actionType,
        visitor_id: visitorId,
        user_agent: userAgent,
        referrer_url: referrerUrl,
        device_type: deviceType,
        metadata: metadata ? JSON.stringify(metadata) : null
      });
  } catch (error) {
    console.error('Error tracking sharing event:', error);
    // Non-critical operation, don't throw
  }
}

/**
 * Track a content view event
 * @param contentType Type of content (game_plan or technique)
 * @param shareId The unique share ID of the content
 * @param metadata Optional additional data to track
 */
export function trackContentView(contentType: ContentType, shareId: string, metadata?: Record<string, unknown>): void {
  trackSharingEvent(contentType, shareId, 'view', metadata).catch(console.error);
}

/**
 * Track a content import event
 * @param contentType Type of content (game_plan or technique)
 * @param shareId The unique share ID of the content
 * @param metadata Optional additional data to track
 */
export function trackContentImport(contentType: ContentType, shareId: string, metadata?: Record<string, unknown>): void {
  trackSharingEvent(contentType, shareId, 'import', metadata).catch(console.error);
}

/**
 * Track a referral conversion event
 * @param contentType Type of content (game_plan or technique)
 * @param shareId The unique share ID of the content
 * @param metadata Optional additional data about the conversion
 */
export function trackReferralConversion(contentType: ContentType, shareId: string, metadata?: Record<string, unknown>): void {
  trackSharingEvent(contentType, shareId, 'conversion', metadata).catch(console.error);
}

/**
 * Get affiliate performance statistics for the current user
 * @param userId The user ID to get stats for
 * @returns A promise that resolves to affiliate performance statistics
 */
export async function getAffiliatePerformanceStats(userId?: string) {
  try {
    console.log('Fetching affiliate stats for user:', userId);
    
    let query = supabase
      .from('affiliate_performance_analytics')
      .select('*');
    
    // If userId is provided, filter the query by referrer_id
    if (userId) {
      query = query.eq('referrer_id', userId);
    }
    
    const { data, error } = await query.maybeSingle();
    
    if (error) {
      console.error('Error fetching affiliate stats:', error);
      throw error;
    }
    
    console.log('Affiliate stats retrieved:', data);
    return data;
  } catch (error) {
    console.error('Error in getAffiliatePerformanceStats:', error);
    throw error;
  }
}

/**
 * Get detailed sharing analytics for a specific referral code
 * @param referralCode The referral code to get analytics for
 * @param timeRange Time range for analytics (last7days, last30days, allTime)
 * @returns A promise that resolves to detailed sharing analytics
 */
export async function getDetailedSharingAnalytics(referralCode: string, timeRange: 'last7days' | 'last30days' | 'allTime' = 'last30days') {
  try {
    let query = supabase
      .from('content_sharing_analytics')
      .select('*')
      .eq('referral_code', referralCode);
    
    // Apply time range filter
    if (timeRange === 'last7days') {
      query = query.gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());
    } else if (timeRange === 'last30days') {
      query = query.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching detailed analytics:', error);
      throw error;
    }
    
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error('Error in getDetailedSharingAnalytics:', error.message);
    }
    throw error;
  }
}

// Export all analytics functions
export default {
  trackSharingEvent,
  trackContentView,
  trackContentImport,
  trackReferralConversion,
  getAffiliatePerformanceStats,
  getDetailedSharingAnalytics,
  getVisitorId,
  getReferralCode
};
